/**
 * My metrics, the starred metrics of the Kennzahlen hub (#2175 B2, D2).
 * Web counterpart of mobile-management/src/features/metrics/favourites.ts: the
 * phone keeps them in device storage, the web in `localStorage` per signed-in
 * user (staff share one browser profile), with the phone's four defaults on
 * first use. Unknown or duplicate ids from an older build are dropped. Server
 * sync is a follow-up.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useOptionalAuthContext } from "@/contexts/AuthContext";
import { DEFAULT_FAVOURITE_METRICS, getMetric } from "./registry";

const STORAGE_PREFIX = "analytics.metrics.favourites.";
const ANONYMOUS_SCOPE = "anon";

export function favouritesStorageKey(userId: string | null | undefined): string {
  return `${STORAGE_PREFIX}${userId || ANONYMOUS_SCOPE}`;
}

/** The stored ids, or the defaults when nothing (or nothing readable) is stored. */
export function readFavourites(storageKey: string): string[] {
  let raw: unknown = null;
  try {
    const text = typeof window === "undefined" ? null : window.localStorage.getItem(storageKey);
    raw = text === null ? null : JSON.parse(text);
  } catch {
    raw = null;
  }
  const list = Array.isArray(raw) ? raw : [...DEFAULT_FAVOURITE_METRICS];
  return list.filter(
    (id, i, all): id is string => typeof id === "string" && !!getMetric(id) && all.indexOf(id) === i,
  );
}

export interface MetricFavourites {
  /** Starred metric ids, in the order they were starred. */
  ids: string[];
  isFavourite: (id: string) => boolean;
  toggle: (id: string) => void;
}

export function useMetricFavourites(): MetricFavourites {
  const auth = useOptionalAuthContext();
  const storageKey = favouritesStorageKey(auth?.userId);
  const [ids, setIds] = useState<string[]>(() => readFavourites(storageKey));

  // The user id resolves after the first render: re-read once the key settles.
  useEffect(() => {
    setIds(readFavourites(storageKey));
  }, [storageKey]);

  const toggle = useCallback(
    (id: string) => {
      setIds((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Storage unavailable: the choice stays for this page view only.
        }
        return next;
      });
    },
    [storageKey],
  );
  const isFavourite = useCallback((id: string) => ids.includes(id), [ids]);
  return useMemo(() => ({ ids, isFavourite, toggle }), [ids, isFavourite, toggle]);
}
